import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, X, FileText, Image, Loader2, Download, Trash2, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// File type icons
const getFileIcon = (contentType) => {
  if (contentType?.startsWith("image/")) {
    return <Image className="w-4 h-4 text-blue-500" />;
  }
  return <FileText className="w-4 h-4 text-red-500" />;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Allowed file types
const ALLOWED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp"
];
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

// Single File Upload Component with drag-and-drop
export const FileUploadZone = ({ 
  onUpload, 
  uploading = false, 
  progress = 0,
  disabled = false,
  multiple = true 
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`${file.name}: Invalid file type. Only PDF and images allowed.`);
      return false;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`${file.name}: File too large. Maximum size is ${MAX_SIZE_MB}MB.`);
      return false;
    }
    return true;
  };

  const handleFiles = (files) => {
    const validFiles = Array.from(files).filter(validateFile);
    if (validFiles.length > 0) {
      onUpload(validFiles);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!disabled && e.dataTransfer.files?.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled]);

  const handleInputChange = (e) => {
    if (e.target.files?.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ""; // Reset input
    }
  };

  return (
    <div
      data-testid="file-upload-zone"
      className={`
        relative border-2 border-dashed rounded-lg p-6 text-center transition-all
        ${isDragging ? "border-[#F37021] bg-orange-50" : "border-gray-300 hover:border-gray-400"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !disabled && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled}
        data-testid="file-input"
      />
      
      {uploading ? (
        <div className="space-y-3">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#F37021]" />
          <p className="text-sm text-gray-600">Uploading...</p>
          <Progress value={progress} className="h-2" />
        </div>
      ) : (
        <div className="space-y-2">
          <Upload className={`w-8 h-8 mx-auto ${isDragging ? "text-[#F37021]" : "text-gray-400"}`} />
          <p className="text-sm font-medium text-gray-700">
            {isDragging ? "Drop files here" : "Click or drag files to upload"}
          </p>
          <p className="text-xs text-gray-500">
            PDF or images only, max {MAX_SIZE_MB}MB per file
          </p>
        </div>
      )}
    </div>
  );
};

// Attachment List Component - displays uploaded files
export const AttachmentList = ({ 
  attachments = [], 
  onDelete,
  canDelete = true,
  currentUserId = null,
  showUploader = true,
  emptyMessage = "No files attached"
}) => {
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const handleDownload = async (attachment) => {
    setDownloading(attachment.id);
    try {
      // Determine download endpoint based on attachment type
      let downloadUrl;
      if (attachment.attachment_level === "agreement") {
        downloadUrl = `${API}/induction-files/${attachment.id}/download`;
      } else {
        downloadUrl = `${API}/request-attachments/${attachment.id}/download`;
      }
      
      const response = await axios.get(downloadUrl, { responseType: "blob" });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download file");
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (attachment) => {
    if (!window.confirm(`Delete ${attachment.filename}?`)) return;
    
    setDeleting(attachment.id);
    try {
      // Determine delete endpoint based on attachment type
      let deleteUrl;
      if (attachment.attachment_level === "agreement") {
        deleteUrl = `${API}/induction-files/${attachment.id}`;
      } else {
        deleteUrl = `${API}/request-attachments/${attachment.id}`;
      }
      
      await axios.delete(deleteUrl);
      toast.success("File deleted");
      if (onDelete) onDelete(attachment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete file");
    } finally {
      setDeleting(null);
    }
  };

  if (attachments.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic py-2">{emptyMessage}</p>
    );
  }

  return (
    <div className="space-y-2" data-testid="attachment-list">
      {attachments.map((attachment) => {
        const canDeleteThis = canDelete && currentUserId && attachment.uploaded_by === currentUserId;
        
        return (
          <div 
            key={attachment.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
            data-testid={`attachment-item-${attachment.id}`}
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {getFileIcon(attachment.content_type)}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate" title={attachment.filename}>
                  {attachment.filename}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatFileSize(attachment.size_bytes)}</span>
                  {showUploader && attachment.uploaded_by_name && (
                    <>
                      <span>•</span>
                      <span className={attachment.uploaded_by_type === "admin" ? "text-blue-600" : "text-green-600"}>
                        {attachment.uploaded_by_type === "admin" ? "Aggreko" : "Gavs"}
                      </span>
                    </>
                  )}
                  {attachment.attachment_level && (
                    <>
                      <span>•</span>
                      <span className={attachment.attachment_level === "agreement" ? "text-purple-600" : "text-orange-600"}>
                        {attachment.attachment_level === "agreement" ? "Agreement" : "Request"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownload(attachment)}
                disabled={downloading === attachment.id}
                data-testid={`download-btn-${attachment.id}`}
              >
                {downloading === attachment.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
              
              {canDeleteThis && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(attachment)}
                  disabled={deleting === attachment.id}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-btn-${attachment.id}`}
                >
                  {deleting === attachment.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Attachment count badge for request cards
export const AttachmentBadge = ({ count = 0 }) => {
  if (count === 0) return null;
  
  return (
    <span 
      className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full"
      data-testid="attachment-badge"
    >
      <Paperclip className="w-3 h-3" />
      {count}
    </span>
  );
};

// Complete File Upload Section for forms
export const FileUploadSection = ({
  requestId,
  attachments = [],
  onAttachmentsChange,
  currentUserId,
  userType, // 'admin' or 'contractor'
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUpload = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    
    const totalFiles = files.length;
    let uploaded = 0;
    const newAttachments = [];
    
    for (const file of files) {
      try {
        // Convert file to base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = reader.result.split(",")[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        
        // Upload to backend
        const response = await axios.post(`${API}/worker-requests/${requestId}/attachments`, {
          filename: file.name,
          content_type: file.type,
          data: base64,
          description: ""
        });
        
        newAttachments.push(response.data);
        uploaded++;
        setUploadProgress(Math.round((uploaded / totalFiles) * 100));
        
      } catch (error) {
        toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || "Unknown error"}`);
      }
    }
    
    if (newAttachments.length > 0) {
      onAttachmentsChange([...attachments, ...newAttachments]);
      toast.success(`${newAttachments.length} file(s) uploaded successfully`);
    }
    
    setUploading(false);
    setUploadProgress(0);
  };

  const handleDelete = (attachmentId) => {
    onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
  };

  return (
    <div className="space-y-4" data-testid="file-upload-section">
      {/* Only show upload zone if requestId exists (for adding files to existing requests) */}
      {requestId && (
        <FileUploadZone
          onUpload={handleUpload}
          uploading={uploading}
          progress={uploadProgress}
          disabled={disabled}
          multiple={true}
        />
      )}
      
      {/* Show existing attachments */}
      {attachments.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Attached Files ({attachments.length})
          </p>
          <AttachmentList
            attachments={attachments}
            onDelete={handleDelete}
            canDelete={true}
            currentUserId={currentUserId}
            showUploader={true}
          />
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;
